import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import ReactModal from 'react-modal';
import { Provider } from 'react-redux';

import App from './App';
import i18n from './i18n/i18n';
import { resetApp } from './store/actions';
import { store } from './store/store';
import ThemeProvider from './ui/organisms/ThemeProvider';
import { ShowEmbedded, ShowWidgetProps } from './utils/_declarations/globals';
import { checkOrDefineSessionId } from './utils/apiCall';
import { getMappedBrandName } from './utils/brandName';

declare global {
  interface Window {
    currentLocation: string;
    embeddedRoot?: Root;
    intervalCheck: any;
    tokenRefreshInterval: number | null;
  }
}

export const unmountApp = () => {
  if (window.embeddedRoot) {
    window.embeddedRoot.unmount();
    delete window.embeddedRoot;
    store.dispatch(resetApp());
  }
};

export const showModalWidget: (props: ShowWidgetProps) => void = ({
  context,
  outputFn,
  parameters,
  vehicle
}) => {
  const rootDiv = document.createElement('div');
  const idRoot = 'LeasysModalWidget-RootComponent';
  document.body.appendChild(rootDiv);

  const root = createRoot(rootDiv as HTMLElement);
  ReactModal.setAppElement(rootDiv as HTMLElement);
  window.embeddedRoot = root;
  checkOrDefineSessionId();
  const { brandName } = context;
  root.render(
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider
          idRoot={idRoot}
          themeName={getMappedBrandName(brandName)}
        >
          <App
            idRoot={idRoot}
            modalVersion
            unmountApp={unmountApp}
            widgetInit={{
              context,
              outputFn,
              parameters,
              vehicle
            }}
          />
        </ThemeProvider>
      </I18nextProvider>
    </Provider>
  );
};

export const showEmbeddedWidget: (props: ShowEmbedded) => void = async ({
  context,
  outputFn,
  parameters,
  targetSelector,
  vehicle
}) => {
  const rootDiv = document.querySelector(`#${targetSelector}`);
  window.currentLocation = `${window.location.origin}${window.location.pathname}`;
  window.intervalCheck = setInterval(() => {
    if (
      window.currentLocation !==
      `${window.location.origin}${window.location.pathname}`
    ) {
      clearInterval(window.intervalCheck);
      if (window.embeddedRoot) {
        window.embeddedRoot.unmount();
        delete window.embeddedRoot;
      }
    }
  }, 50);

  if (rootDiv) {
    if (window.embeddedRoot && rootDiv.innerHTML !== '') {
      const event = new CustomEvent('LeasysEventUpdatedWidget', {
        detail: { context, outputFn, parameters, targetSelector, vehicle }
      });
      document.dispatchEvent(event);
    } else {
      const root = createRoot(rootDiv as HTMLElement);

      window.embeddedRoot = root;

      checkOrDefineSessionId();

      const { brandName } = context;

      root.render(
        <Provider store={store}>
          <I18nextProvider i18n={i18n}>
            <ThemeProvider
              forceMobile
              idRoot={targetSelector}
              themeName={getMappedBrandName(brandName)}
            >
              <App
                unmountApp={unmountApp}
                widgetInit={{
                  context,
                  outputFn,
                  parameters,
                  vehicle
                }}
              />
            </ThemeProvider>
          </I18nextProvider>
        </Provider>
      );
    }
  }
};

declare global {
  interface Window {
    showEmbeddedWidget: (props: ShowEmbedded) => void;
    showModalWidget: (props: ShowWidgetProps) => void;
  }
}
if (process.env.REACT_APP_TARGET_BUILD === 'demo') {
  window.showModalWidget = showModalWidget;
  window.showEmbeddedWidget = showEmbeddedWidget;
}
